import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, ButtonGroup, Grid, CircularProgress } from '@mui/material';

import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';

import PagedTable from 'components/table/PagedTable';
import { filterSelector, bookingModeSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';
import { EBookingMode } from 'types';

import { SERVICETYPES_LIST_QUERY } from '../gql';

import { ServiceTypeCopyButton, ServiceTypeDeleteButton } from './servicetype';
import LocalizedSkuText from '../LocalizedSkuText';

/**
 * Service types list page with pagination and filtering by space and booking mode.
 * Supports copy and delete actions for service types.
 */
export default function ServiceTypesList() {
  const navigate = useNavigate();
  const filter = filterSelector();
  const bookingMode = bookingModeSelector();
  const user = userSelector()!;

  const [query, { data, loading, error }] = useLazyQuery(SERVICETYPES_LIST_QUERY);

  // Determine filter based on mode: Epilo mode (isSystem = true) vs SEM mode (isSystem = false)
  const isSystemFilter = bookingMode === EBookingMode.EPILO;

  return (
    <>
      <Helmet>
        <title>{i18next.t('servicetypes-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PagedTable
            filterKey={`${JSON.stringify(filter)}_${isSystemFilter}`}
            refetchPage={(skip, take) =>
              query({
                variables: {
                  spaceId: (filter && filter.spaceId) || null,
                  hotelId: isSystemFilter ? ((filter && filter.hotelId) || null) : null,
                  where: {
                    isSystem: isSystemFilter,
                  },
                  skip,
                  take,
                },
              })
            }
            rowsLoading={loading}
            rowsErr={error}
            headers={[
              i18next.t('servicetypes-list-header-type'),
              i18next.t('servicetypes-list-header-sku'),
              i18next.t('servicetypes-list-header-sequence'),
              i18next.t('servicetypes-list-header-ispublished'),
              ...(user.isSingleSpace ? [] : [i18next.t('list-header-space')]),
              '',
            ]}
            totalCount={data?.listServiceTypes?.totalCount}
            rows={(data?.listServiceTypes?.serviceTypes || []).map(row => [
              <Link to={`/servicetypes/${row.id}`}>
                <LocalizedSkuText
                  sku={row.sku}
                  fallback={row.name}
                  hotelId={filter?.hotelId || null}
                  spaceId={filter?.spaceId || row.space.id}
                />
              </Link>,
              row.sku,
              row.sequence,
              row.isPublished && <CheckIcon />,
              ...(user.isSingleSpace ? [] : [row.space.name]),
              <ButtonGroup>
                <ServiceTypeCopyButton id={row.id} spaceId={row.space.id} isSystem={row.isSystem} icon />
                {(user.isRoot || !isSystemFilter) && <ServiceTypeDeleteButton id={row.id} spaceId={row.space.id} isSystem={row.isSystem} icon />}
              </ButtonGroup>,
            ])}
          />
        </Grid>
        {hasAnyAdminSpace(user) && (user.isRoot || (!isSystemFilter && (user.isSeminargo || bookingMode === EBookingMode.SEM))) && (
          <Grid item xs={12}>
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!filter?.spaceId}
              onClick={() => navigate('/servicetypes/_create')}
            >
              {i18next.t('servicetypes-list-add')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
}
